import http from '@frontend/api/http';
import { OPAL_URL } from '@frontend/envConfig';

import type {
  BulkUpdateResponse,
  PaginatedGroupedNotificationsResponse,
  PaginatedNotificationsResponse,
  UnreadCountResponse,
} from '../types';

const BASE_URL = `${OPAL_URL}/notification-service/api/v1/notifications`;

export type FetchOffsetNotificationsParams = {
  limit: number;
  offset: number;
  is_read?: boolean;
  agent_id?: string;
  category?: string;
  priority?: string;
  created_after?: string;
  created_before?: string;
  action_required?: boolean;
};

export type FetchCursorNotificationsParams = {
  limit: number;
  cursor?: string | null;
  is_read?: boolean;
  agent_id?: string;
  category?: string;
  priority?: string;
  created_after?: string;
  created_before?: string;
  action_required?: boolean;
};

function appendListFilters(
  searchParams: URLSearchParams,
  params: {
    is_read?: boolean;
    agent_id?: string;
    category?: string;
    priority?: string;
    created_after?: string;
    created_before?: string;
    action_required?: boolean;
  }
) {
  if (params.is_read !== undefined) searchParams.set('is_read', params.is_read.toString());
  if (params.agent_id) searchParams.set('agent_id', params.agent_id);
  if (params.category) searchParams.set('category', params.category);
  if (params.priority) searchParams.set('priority', params.priority);
  if (params.created_after) searchParams.set('created_after', params.created_after);
  if (params.created_before) searchParams.set('created_before', params.created_before);
  if (params.action_required) searchParams.set('action_required', 'true');
}

export async function fetchOffsetNotifications(params: FetchOffsetNotificationsParams) {
  const searchParams = new URLSearchParams({
    limit: params.limit.toString(),
    offset: params.offset.toString(),
  });
  appendListFilters(searchParams, params);

  const res = await http.get<PaginatedNotificationsResponse>(`${BASE_URL}?${searchParams}`);
  return res.data;
}

export async function fetchGroupedNotifications(params: FetchOffsetNotificationsParams) {
  const searchParams = new URLSearchParams({
    limit: params.limit.toString(),
    offset: params.offset.toString(),
    grouped: 'true',
  });
  appendListFilters(searchParams, params);

  const res = await http.get<PaginatedGroupedNotificationsResponse>(`${BASE_URL}?${searchParams}`);
  return res.data;
}

export async function fetchCursorNotifications(params: FetchCursorNotificationsParams) {
  const searchParams = new URLSearchParams({
    limit: params.limit.toString(),
  });
  if (params.cursor) searchParams.set('cursor', params.cursor);
  appendListFilters(searchParams, params);

  const res = await http.get<PaginatedNotificationsResponse>(`${BASE_URL}?${searchParams}`);
  return res.data;
}

export async function fetchCursorGroupedNotifications(params: FetchCursorNotificationsParams) {
  const searchParams = new URLSearchParams({
    limit: params.limit.toString(),
    grouped: 'true',
  });
  if (params.cursor) searchParams.set('cursor', params.cursor);
  appendListFilters(searchParams, params);

  const res = await http.get<PaginatedGroupedNotificationsResponse>(`${BASE_URL}?${searchParams}`);
  return res.data;
}

export async function fetchUnreadCount(grouped = false) {
  const url = grouped ? `${BASE_URL}/unread-count?grouped=true` : `${BASE_URL}/unread-count`;
  const res = await http.get<UnreadCountResponse>(url);
  return res.data;
}

export async function fetchActionRequiredCount(grouped = false) {
  const url = grouped
    ? `${BASE_URL}/action-required-count?grouped=true`
    : `${BASE_URL}/action-required-count`;
  const res = await http.get<UnreadCountResponse>(url);
  return res.data;
}

export async function fetchBadgeCount(grouped = false) {
  const url = grouped ? `${BASE_URL}/badge?grouped=true` : `${BASE_URL}/badge`;
  const res = await http.get<UnreadCountResponse>(url);
  return res.data;
}

export async function markBadgeSeen() {
  await http.post(`${BASE_URL}/badge/mark-seen`);
}

export async function markAsRead(id: string) {
  await http.patch(`${BASE_URL}/${id}/read`);
}

export async function markAsUnread(id: string) {
  await http.patch(`${BASE_URL}/${id}/unread`);
}

export async function markActionCompleted(id: string) {
  await http.patch(`${BASE_URL}/${id}/action-complete`);
}

export async function bulkMarkAsRead(params?: { ids?: string[]; group_id?: string }) {
  const res = await http.patch<BulkUpdateResponse>(`${BASE_URL}/bulk-read`, params);
  return res.data;
}

export async function bulkMarkAsUnread(params?: { ids?: string[]; group_id?: string }) {
  const res = await http.patch<BulkUpdateResponse>(`${BASE_URL}/bulk-unread`, params);
  return res.data;
}

export async function deleteNotification(id: string) {
  await http.delete(`${BASE_URL}/${id}`);
}

export async function bulkDelete(params?: { ids?: string[]; group_id?: string }) {
  const res = await http.post<BulkUpdateResponse>(`${BASE_URL}/bulk-delete`, params);
  return res.data;
}
